
import React from 'react';
import { useTranslation } from 'react-i18next';

const ServicesSection: React.FC = () => {
  const { t } = useTranslation();
  const services = [
    {
      icon: 'ri-global-line',
      title: t('services.items.web.title'),
      description: t('services.items.web.desc'),
      features: t('services.items.web.features', { returnObjects: true }) as string[],
      image: 'https://readdy.ai/api/search-image?query=Modern%20responsive%20website%20development%20on%20multiple%20devices%2C%20clean%20interface%20design%2C%20professional%20web%20development%20workspace%2C%20coding%20on%20laptop%2C%20mobile%20and%20desktop%20screens%20showing%20website%2C%20blue%20and%20white%20theme&width=400&height=300&seq=service-web-001&orientation=landscape'
    },
    {
      icon: 'ri-smartphone-line',
      title: t('services.items.mini.title'),
      description: t('services.items.mini.desc'),
      features: t('services.items.mini.features', { returnObjects: true }) as string[],
      image: 'https://readdy.ai/api/search-image?query=Mobile%20app%20development%20and%20mini%20program%20interface%20design%2C%20smartphone%20showing%20mini%20program%20interface%2C%20clean%20modern%20UI%20design%2C%20developer%20working%20on%20mobile%20app%2C%20professional%20development%20environment&width=400&height=300&seq=service-mini-001&orientation=landscape'
    },
    {
      icon: 'ri-phone-line',
      title: t('services.items.app.title'),
      description: t('services.items.app.desc'),
      features: t('services.items.app.features', { returnObjects: true }) as string[],
      image: 'https://readdy.ai/api/search-image?query=Mobile%20application%20development%20process%2C%20iOS%20and%20Android%20app%20interfaces%2C%20professional%20app%20developer%20coding%2C%20smartphone%20and%20tablet%20showing%20mobile%20apps%2C%20modern%20development%20workspace&width=400&height=300&seq=service-app-001&orientation=landscape'
    },
    {
      icon: 'ri-code-s-slash-line',
      title: t('services.items.migration.title'),
      description: t('services.items.migration.desc'),
      features: t('services.items.migration.features', { returnObjects: true }) as string[],
      image: 'https://readdy.ai/api/search-image?query=Code%20migration%20from%20PHP%20to%20Go%20programming%20language%2C%20developer%20working%20on%20code%20refactoring%2C%20multiple%20monitors%20showing%20code%20comparison%2C%20professional%20programming%20environment%2C%20clean%20workspace&width=400&height=300&seq=service-migration-001&orientation=landscape'
    }
  ];

  return (
    <section id="services" className="py-20 bg-gray-50">
      <div className="container mx-auto px-6">
        <div className="text-center mb-16">
          <h2 className="text-4xl font-bold text-gray-900 mb-4">
            {t('services.title')}
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            {t('services.intro')}
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          {services.map((service, index) => (
            <div 
              key={index}
              className="bg-white rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 overflow-hidden group"
            >
              <div className="relative h-48 overflow-hidden">
                <img 
                  src={service.image}
                  alt={service.title}
                  className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-300"
                />
                <div className="absolute top-4 left-4 w-12 h-12 bg-blue-600 rounded-full flex items-center justify-center">
                  <i className={`${service.icon} text-white text-xl`}></i>
                </div>
              </div>
              
              <div className="p-6">
                <h3 className="text-2xl font-bold text-gray-900 mb-3">
                  {service.title}
                </h3>
                <p className="text-gray-600 mb-4 leading-relaxed">
                  {service.description}
                </p>
                
                <div className="grid grid-cols-2 gap-2">
                  {service.features.map((feature, featureIndex) => (
                    <div key={featureIndex} className="flex items-center">
                      <div className="w-4 h-4 flex items-center justify-center mr-2">
                        <i className="ri-check-line text-green-500 text-sm"></i>
                      </div>
                      <span className="text-sm text-gray-700">{feature}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Technology Stack */}
        <div className="mt-16 text-center">
          <h3 className="text-2xl font-bold text-gray-900 mb-8">{t('services.techTitle')}</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-6">
            {[
              { name: 'React', icon: 'ri-reactjs-line' },
              { name: 'Vue.js', icon: 'ri-vuejs-line' },
              { name: 'Node.js', icon: 'ri-nodejs-line' },
              { name: 'Go', icon: 'ri-code-line' },
              { name: 'PHP', icon: 'ri-code-s-slash-line' },
              { name: 'MySQL', icon: 'ri-database-2-line' }
            ].map((tech, index) => (
              <div 
                key={index}
                className="bg-white p-4 rounded-lg shadow-md hover:shadow-lg transition-shadow duration-300"
              >
                <div className="w-8 h-8 flex items-center justify-center mx-auto mb-2">
                  <i className={`${tech.icon} text-2xl text-blue-600`}></i>
                </div>
                <p className="text-sm font-medium text-gray-700">{tech.name}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default ServicesSection;
